import React, { useState, useEffect } from 'react'
import './SimulacaoFotus.css'

// Base fixa da API Fotus. Em dev usa proxy (/api/fotus) para evitar CORS.
const FOTUS_API_BASE = 'https://api-d0983.cloud.solaryum.com.br/integracaoPlataforma'
const isDev = process.env.NODE_ENV === 'development'
const API_BUSCAR_FILTROS = isDev ? '/api/fotus/BuscarFiltros' : `${FOTUS_API_BASE}/BuscarFiltros`
const API_BUSCAR_KITS = isDev ? '/api/fotus/BuscarKits' : `${FOTUS_API_BASE}/BuscarKits`
const API_MONTAR_KITS = isDev ? '/api/fotus/MontarKits' : `${FOTUS_API_BASE}/MontarKits`
const API_TOKEN = process.env.REACT_APP_FOTUS_TOKEN || 'zXM4kyHS'
const API_AUTH_HEADER = process.env.REACT_APP_FOTUS_AUTH || '2@$phMn@$zSdsa'
const DEFAULT_IBGE = process.env.REACT_APP_FOTUS_IBGE || '1500800'

const OPCOES_TENSAO = [
  { value: '1', label: '220' },
  { value: '2', label: '380' },
  { value: '3', label: '+380' }
]
const OPCOES_FASE = [
  { value: '0', label: 'Monofásico' },
  { value: '2', label: 'Trifásico' }
]
const OPCOES_TIPO_INV = [
  { value: '0', label: 'String' },
  { value: '1', label: 'Micro' },
  { value: '2', label: 'Híbrido' }
]

/**
 * Simulação Fotus – tela apenas para administrador.
 * Consome a API de filtros (marcas de painéis, inversores, tipos de telhado, potências) e exibe os campos.
 */
const SimulacaoFotus = ({ onBack }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filtros, setFiltros] = useState({
    marcasPaineis: [],
    marcasInversores: [],
    tiposTelhados: [],
    potenciasPaineis: []
  })
  const [kits, setKits] = useState([])
  const [errorKits, setErrorKits] = useState(null)
  const [loadingKits, setLoadingKits] = useState(false)
  const [kitsPersonalizados, setKitsPersonalizados] = useState([])
  const [errorKitsPersonalizados, setErrorKitsPersonalizados] = useState(null)
  const [loadingKitsPersonalizados, setLoadingKitsPersonalizados] = useState(false)
  const [modalKit, setModalKit] = useState(null)
  const [activeTab, setActiveTab] = useState('disponiveis')

  const [form, setForm] = useState({
    idMarcaPainel: '',
    idMarcaInversor: '',
    idTipoTelhado: '',
    potenciaPainel: '',
    potenciaDoKit: '',
    tensao: '',
    fase: '',
    tipoInv: ''
  })

  useEffect(() => {
    carregarFiltros()
  }, [])

  const carregarFiltros = async () => {
    setLoading(true)
    setError(null)
    try {
      const url = `${API_BUSCAR_FILTROS}?token=${encodeURIComponent(API_TOKEN)}`
      const headers = { Accept: 'text/plain', Authorization: API_AUTH_HEADER }
      const res = await fetch(url, { method: 'GET', headers })
      // API retorna text/plain (pode ser JSON em texto)
      const text = await res.text()
      const raw = (() => {
        try {
          return text ? JSON.parse(text) : {}
        } catch {
          return {}
        }
      })()
      if (!res.ok) {
        const msg = raw?.message || raw?.mensagem || raw?.error || raw?.erro || raw?.msg || (typeof raw === 'string' ? raw : null)
        const detail = msg || (text || `Erro na API: ${res.status} ${res.statusText}`)
        throw new Error(detail)
      }
      // API pode retornar direto ou dentro de uma chave (ex.: data)
      const data = raw?.data != null ? raw.data : raw
      setFiltros({
        marcasPaineis: Array.isArray(data?.marcasPaineis) ? data.marcasPaineis : [],
        marcasInversores: Array.isArray(data?.marcasInversores) ? data.marcasInversores : [],
        tiposTelhados: Array.isArray(data?.tiposTelhados) ? data.tiposTelhados : [],
        potenciasPaineis: Array.isArray(data?.potenciasPaineis) ? data.potenciasPaineis : []
      })
    } catch (err) {
      console.error('Erro ao buscar filtros Fotus:', err)
      setError(err.message || 'Falha ao carregar filtros.')
    } finally {
      setLoading(false)
    }
  }

  /** Monta os query params comuns a BuscarKits e MontarKits a partir do formulário. */
  const buildParamsKits = () => {
    const params = new URLSearchParams()
    params.set('token', API_TOKEN)
    params.set('ibge', DEFAULT_IBGE)
    params.set('cifComDescarga', 'false')
    if (form.potenciaPainel !== '' && form.potenciaPainel != null) {
      params.set('potenciaDoPainel', String(form.potenciaPainel))
    }
    const marcaPainelDesc = filtros.marcasPaineis.find(m => String(m.idMarca) === String(form.idMarcaPainel))?.descricao
    if (marcaPainelDesc) params.set('marcaPainel', marcaPainelDesc)
    const marcaInversorDesc = filtros.marcasInversores.find(m => String(m.idMarca) === String(form.idMarcaInversor))?.descricao
    if (marcaInversorDesc) params.set('marcaInversor', marcaInversorDesc)
    const telhadoDesc = filtros.tiposTelhados.find(t => String(t.id) === String(form.idTipoTelhado))?.descricao
    if (telhadoDesc) params.set('telhados', telhadoDesc)
    if (form.potenciaDoKit !== '' && form.potenciaDoKit != null) params.set('potenciaDoKit', String(form.potenciaDoKit))
    if (form.tensao !== '' && form.tensao != null) params.set('tensao', String(form.tensao))
    if (form.fase !== '' && form.fase != null) params.set('fase', String(form.fase))
    if (form.tipoInv !== '' && form.tipoInv != null) params.set('tipoInv', String(form.tipoInv))
    return params
  }

  /** Busca kits disponíveis (BuscarKits). */
  const carregarKits = async () => {
    setLoadingKits(true)
    setErrorKits(null)
    try {
      const params = buildParamsKits()
      const url = `${API_BUSCAR_KITS}?${params}`
      const headers = { Accept: 'text/plain', Authorization: API_AUTH_HEADER }
      const res = await fetch(url, { method: 'GET', headers })
      const text = await res.text()
      let list = []
      try {
        list = text ? JSON.parse(text) : []
      } catch {
        list = []
      }
      if (!res.ok) {
        const msg = (typeof list === 'object' && list?.message) || list?.mensagem || text || `Erro na API: ${res.status}`
        throw new Error(msg)
      }
      setKits(Array.isArray(list) ? list : [])
    } catch (err) {
      console.error('Erro ao buscar kits Fotus:', err)
      setErrorKits(err.message || 'Falha ao carregar kits.')
      setKits([])
    } finally {
      setLoadingKits(false)
    }
  }

  /** Busca kits personalizados (MontarKits) – usada quando a aba Kits Personalizados está ativa. */
  const carregarKitsPersonalizados = async () => {
    setLoadingKitsPersonalizados(true)
    setErrorKitsPersonalizados(null)
    try {
      const params = buildParamsKits()
      const url = `${API_MONTAR_KITS}?${params}`
      const headers = { Accept: 'text/plain', Authorization: API_AUTH_HEADER }
      const res = await fetch(url, { method: 'GET', headers })
      const text = await res.text()
      let list = []
      try {
        list = text ? JSON.parse(text) : []
      } catch {
        list = []
      }
      if (!res.ok) {
        const msg = (typeof list === 'object' && list?.message) || list?.mensagem || text || `Erro na API: ${res.status}`
        throw new Error(msg)
      }
      setKitsPersonalizados(Array.isArray(list) ? list : [])
    } catch (err) {
      console.error('Erro ao montar kits Fotus:', err)
      setErrorKitsPersonalizados(err.message || 'Falha ao montar kits personalizados.')
      setKitsPersonalizados([])
    } finally {
      setLoadingKitsPersonalizados(false)
    }
  }

  /** Dispara a busca conforme a aba ativa (disponíveis = BuscarKits, personalizados = MontarKits). */
  const handleBuscarKits = () => {
    if (activeTab === 'personalizados') {
      carregarKitsPersonalizados()
    } else {
      carregarKits()
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  if (loading) {
    return (
      <div className="simulacao-fotus">
        <div className="simulacao-fotus-header">
          <button type="button" className="back-button" onClick={onBack} aria-label="Voltar">
            ← Voltar
          </button>
          <h1 className="simulacao-fotus-title">Simulação Fotus</h1>
        </div>
        <div className="simulacao-fotus-loading">
          <span className="spinner-border text-primary" role="status" aria-hidden="true" />
          <p>Carregando filtros...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="simulacao-fotus">
        <div className="simulacao-fotus-header">
          <button type="button" className="back-button" onClick={onBack} aria-label="Voltar">
            ← Voltar
          </button>
          <h1 className="simulacao-fotus-title">Simulação Fotus</h1>
        </div>
        <div className="simulacao-fotus-error alert alert-danger">
          <p>{error}</p>
          <button type="button" className="btn btn-outline-danger" onClick={carregarFiltros}>
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  const { marcasPaineis, marcasInversores, tiposTelhados, potenciasPaineis } = filtros
  const potenciasOrdenadas = [...potenciasPaineis]
    .filter(p => p.potencia != null)
    .sort((a, b) => (a.potencia || 0) - (b.potencia || 0))

  return (
    <div className="simulacao-fotus">
      <div className="simulacao-fotus-header">
        <button type="button" className="back-button" onClick={onBack} aria-label="Voltar">
          ← Voltar
        </button>
        <h1 className="simulacao-fotus-title">Simulação Fotus</h1>
      </div>

      <div className="simulacao-fotus-tabs" role="tablist" aria-label="Tipos de kits">
        <button
          type="button"
          role="tab"
          id="tab-disponiveis"
          className={`simulacao-fotus-tab ${activeTab === 'disponiveis' ? 'active' : ''}`}
          onClick={() => setActiveTab('disponiveis')}
          aria-selected={activeTab === 'disponiveis'}
          aria-controls="panel-disponiveis"
        >
          Kits disponíveis
        </button>
        <button
          type="button"
          role="tab"
          id="tab-personalizados"
          className={`simulacao-fotus-tab ${activeTab === 'personalizados' ? 'active' : ''}`}
          onClick={() => setActiveTab('personalizados')}
          aria-selected={activeTab === 'personalizados'}
          aria-controls="panel-personalizados"
        >
          Kits Personalizados
        </button>
      </div>

      <div className="simulacao-fotus-content">
        <p className="simulacao-fotus-desc">
          Selecione os filtros abaixo. Os dados são carregados da API de integração e utilizados nas duas abas.
        </p>

        <form className="simulacao-fotus-form">
          <div className="simulacao-fotus-grid">
            <div className="simulacao-fotus-field">
              <label htmlFor="idMarcaPainel">Marca do painel</label>
              <select
                id="idMarcaPainel"
                name="idMarcaPainel"
                value={form.idMarcaPainel}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Selecione...</option>
                {marcasPaineis.map((m) => (
                  <option key={m.idMarca} value={m.idMarca}>
                    {m.descricao || `ID ${m.idMarca}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="simulacao-fotus-field">
              <label htmlFor="idMarcaInversor">Marca do inversor</label>
              <select
                id="idMarcaInversor"
                name="idMarcaInversor"
                value={form.idMarcaInversor}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Selecione...</option>
                {marcasInversores.map((m) => (
                  <option key={m.idMarca} value={m.idMarca}>
                    {m.descricao || `ID ${m.idMarca}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="simulacao-fotus-field">
              <label htmlFor="idTipoTelhado">Tipo de telhado</label>
              <select
                id="idTipoTelhado"
                name="idTipoTelhado"
                value={form.idTipoTelhado}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Selecione...</option>
                {tiposTelhados.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.descricao || `ID ${t.id}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="simulacao-fotus-field">
              <label htmlFor="potenciaPainel">Potência do painel (W)</label>
              <select
                id="potenciaPainel"
                name="potenciaPainel"
                value={form.potenciaPainel}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Selecione...</option>
                {potenciasOrdenadas.map((p, idx) => (
                  <option key={p.potencia !== undefined ? p.potencia : idx} value={p.potencia}>
                    {p.potencia === 0 ? '0 (não informado)' : `${p.potencia} W`}
                  </option>
                ))}
              </select>
            </div>

            <div className="simulacao-fotus-field">
              <label htmlFor="potenciaDoKit">Potência do kit (kW)</label>
              <input
                id="potenciaDoKit"
                name="potenciaDoKit"
                type="number"
                min="0"
                step="0.1"
                value={form.potenciaDoKit}
                onChange={handleChange}
                className="form-control"
                placeholder="Ex.: 10"
              />
            </div>

            <div className="simulacao-fotus-field">
              <label htmlFor="tensao">Tensão</label>
              <select
                id="tensao"
                name="tensao"
                value={form.tensao}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Selecione...</option>
                {OPCOES_TENSAO.map((o) => (
                  <option key={o.value} value={o.value}>{o.label} V</option>
                ))}
              </select>
            </div>

            <div className="simulacao-fotus-field">
              <label htmlFor="fase">Fase</label>
              <select
                id="fase"
                name="fase"
                value={form.fase}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Selecione...</option>
                {OPCOES_FASE.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="simulacao-fotus-field">
              <label htmlFor="tipoInv">Tipo de inversor</label>
              <select
                id="tipoInv"
                name="tipoInv"
                value={form.tipoInv}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">Selecione...</option>
                {OPCOES_TIPO_INV.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="simulacao-fotus-form-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleBuscarKits}
              disabled={loadingKits || loadingKitsPersonalizados}
            >
              {(loadingKits || loadingKitsPersonalizados) ? 'Buscando...' : 'Buscar kits'}
            </button>
          </div>
        </form>

        {activeTab === 'disponiveis' && (
        <section id="panel-disponiveis" role="tabpanel" aria-labelledby="tab-disponiveis" className="simulacao-fotus-kits" aria-label="Kits disponíveis">
          <h2 className="simulacao-fotus-kits-title">Kits disponíveis</h2>
          {loadingKits && (
            <div className="simulacao-fotus-kits-loading">
              <span className="spinner-border text-primary" role="status" aria-hidden="true" />
              <p>Carregando kits...</p>
            </div>
          )}
          {!loadingKits && errorKits && (
            <div className="simulacao-fotus-kits-error alert alert-warning">
              <p>{errorKits}</p>
              <button type="button" className="btn btn-outline-secondary" onClick={carregarKits}>
                Tentar novamente
              </button>
            </div>
          )}
          {!loadingKits && !errorKits && kits.length === 0 && (
            <p className="simulacao-fotus-kits-empty">
              Selecione os filtros e clique em &quot;Buscar kits&quot; para carregar os resultados.
            </p>
          )}
          {!loadingKits && !errorKits && kits.length > 0 && (
            <>
              <p className="simulacao-fotus-kits-count">{kits.length} kit(s) encontrado(s)</p>
              <div className="simulacao-fotus-kits-grid">
              {kits.map((kit) => (
                <article key={kit.idProduto ?? kit.codErp ?? Math.random()} className="simulacao-fotus-kit-card">
                  {kit.fotoUrl && (
                    <div className="simulacao-fotus-kit-card-img">
                      <img src={kit.fotoUrl} alt="" loading="lazy" />
                    </div>
                  )}
                  <div className="simulacao-fotus-kit-card-body">
                    <h3 className="simulacao-fotus-kit-card-title">
                      {kit.descricao || kit.modelo || `Kit #${kit.idProduto}`}
                    </h3>
                    {(kit.marca || kit.modelo) && (
                      <p className="simulacao-fotus-kit-card-marca">
                        {[kit.marca, kit.modelo].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    <div className="simulacao-fotus-kit-card-meta">
                      {kit.potencia != null && kit.potencia > 0 && (
                        <span className="simulacao-fotus-kit-card-badge">{(kit.potencia)} W</span>
                      )}
                      {kit.categoria && (
                        <span className="simulacao-fotus-kit-card-badge">{kit.categoria}</span>
                      )}
                      {kit.agrupamento && (
                        <span className="simulacao-fotus-kit-card-badge">{kit.agrupamento}</span>
                      )}
                    </div>
                    {kit.precoVenda != null && kit.precoVenda > 0 && (
                      <p className="simulacao-fotus-kit-card-preco">
                        R$ {Number(kit.precoVenda).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    )}
                    {Array.isArray(kit.composicao) && kit.composicao.length > 0 && (
                      <p className="simulacao-fotus-kit-card-composicao">
                        {kit.composicao.length} item(ns) na composição
                      </p>
                    )}
                    {kit.marcaPainel && (
                      <p className="simulacao-fotus-kit-card-detail">Painel: {kit.marcaPainel}</p>
                    )}
                    {kit.marcaInversor && (
                      <p className="simulacao-fotus-kit-card-detail">Inversor: {kit.marcaInversor}</p>
                    )}
                    {kit.dataSheet && (
                      <a href={kit.dataSheet} target="_blank" rel="noopener noreferrer" className="simulacao-fotus-kit-card-link">
                        Ficha técnica
                      </a>
                    )}
                    <button
                      type="button"
                      className="simulacao-fotus-kit-card-btn"
                      onClick={() => setModalKit(kit)}
                    >
                      Kit
                    </button>
                  </div>
                </article>
              ))}
              </div>
            </>
          )}
        </section>
        )}

        {activeTab === 'personalizados' && (
          <section id="panel-personalizados" role="tabpanel" aria-labelledby="tab-personalizados" className="simulacao-fotus-kits simulacao-fotus-tab-panel" aria-label="Kits Personalizados">
            <h2 className="simulacao-fotus-kits-title">Kits Personalizados</h2>
            {loadingKitsPersonalizados && (
              <div className="simulacao-fotus-kits-loading">
                <span className="spinner-border text-primary" role="status" aria-hidden="true" />
                <p>Montando kits...</p>
              </div>
            )}
            {!loadingKitsPersonalizados && errorKitsPersonalizados && (
              <div className="simulacao-fotus-kits-error alert alert-warning">
                <p>{errorKitsPersonalizados}</p>
                <button type="button" className="btn btn-outline-secondary" onClick={carregarKitsPersonalizados}>
                  Tentar novamente
                </button>
              </div>
            )}
            {!loadingKitsPersonalizados && !errorKitsPersonalizados && kitsPersonalizados.length === 0 && (
              <p className="simulacao-fotus-kits-empty">
                Selecione os filtros e clique em &quot;Buscar kits&quot; para montar kits personalizados.
              </p>
            )}
            {!loadingKitsPersonalizados && !errorKitsPersonalizados && kitsPersonalizados.length > 0 && (
              <>
                <p className="simulacao-fotus-kits-count">{kitsPersonalizados.length} kit(s) montado(s)</p>
                <div className="simulacao-fotus-kits-grid">
                  {kitsPersonalizados.map((kit) => (
                    <article key={kit.idProduto ?? kit.codErp ?? Math.random()} className="simulacao-fotus-kit-card">
                      {kit.fotoUrl && (
                        <div className="simulacao-fotus-kit-card-img">
                          <img src={kit.fotoUrl} alt="" loading="lazy" />
                        </div>
                      )}
                      <div className="simulacao-fotus-kit-card-body">
                        <h3 className="simulacao-fotus-kit-card-title">
                          {kit.descricao || kit.modelo || `Kit #${kit.idProduto}`}
                        </h3>
                        {(kit.marca || kit.modelo) && (
                          <p className="simulacao-fotus-kit-card-marca">
                            {[kit.marca, kit.modelo].filter(Boolean).join(' · ')}
                          </p>
                        )}
                        <div className="simulacao-fotus-kit-card-meta">
                          {kit.potencia != null && kit.potencia > 0 && (
                            <span className="simulacao-fotus-kit-card-badge">{kit.potencia} W</span>
                          )}
                          {kit.categoria && (
                            <span className="simulacao-fotus-kit-card-badge">{kit.categoria}</span>
                          )}
                          {kit.agrupamento && (
                            <span className="simulacao-fotus-kit-card-badge">{kit.agrupamento}</span>
                          )}
                        </div>
                        {kit.precoVenda != null && kit.precoVenda > 0 && (
                          <p className="simulacao-fotus-kit-card-preco">
                            R$ {Number(kit.precoVenda).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        )}
                        {Array.isArray(kit.composicao) && kit.composicao.length > 0 && (
                          <p className="simulacao-fotus-kit-card-composicao">
                            {kit.composicao.length} item(ns) na composição
                          </p>
                        )}
                        {kit.marcaPainel && (
                          <p className="simulacao-fotus-kit-card-detail">Painel: {kit.marcaPainel}</p>
                        )}
                        {kit.marcaInversor && (
                          <p className="simulacao-fotus-kit-card-detail">Inversor: {kit.marcaInversor}</p>
                        )}
                        {kit.dataSheet && (
                          <a href={kit.dataSheet} target="_blank" rel="noopener noreferrer" className="simulacao-fotus-kit-card-link">
                            Ficha técnica
                          </a>
                        )}
                        <button
                          type="button"
                          className="simulacao-fotus-kit-card-btn"
                          onClick={() => setModalKit(kit)}
                        >
                          Kit
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              </>
            )}
          </section>
        )}
      </div>

      {modalKit && (
        <div className="simulacao-fotus-modal-overlay" onClick={() => setModalKit(null)} role="dialog" aria-modal="true" aria-labelledby="modal-kit-title">
          <div className="simulacao-fotus-modal" onClick={(e) => e.stopPropagation()}>
            <div className="simulacao-fotus-modal-header">
              <h2 id="modal-kit-title" className="simulacao-fotus-modal-title">
                {modalKit.descricao || modalKit.modelo || `Kit #${modalKit.idProduto}`}
              </h2>
              <button type="button" className="simulacao-fotus-modal-close" onClick={() => setModalKit(null)} aria-label="Fechar">
                ×
              </button>
            </div>
            <div className="simulacao-fotus-modal-body">
              <p className="simulacao-fotus-modal-subtitle">Composição do kit – produto e quantidade</p>
              {Array.isArray(modalKit.composicao) && modalKit.composicao.length > 0 ? (
                <div className="simulacao-fotus-modal-table-wrap">
                  <table className="simulacao-fotus-modal-table">
                    <thead>
                      <tr>
                        <th>Produto</th>
                        <th>Quant.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalKit.composicao.map((item, idx) => (
                        <tr key={item.idProduto ?? idx}>
                          <td data-label="Produto">{item.descricao || `#${item.idProduto}`}</td>
                          <td data-label="Quant.">{item.qtd != null ? item.qtd : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="simulacao-fotus-modal-empty">Nenhum item na composição.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SimulacaoFotus
